import { useAtomValue, useSetAtom } from 'jotai';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/core/ui';
import { MapMarker } from '@/features/map';
import { useUnitFormatter } from '@/features/units';
import { isEmpty, margin, pathLength, sampleAt } from '../model/result';
import { useOriginActionLabel } from '../panel/useOriginActionLabel';
import { computeConeAtom } from '../state/actions';
import { inspectionAtom, resultAtom, statusAtom } from '../state/atoms';

/** Read-outs drawn on the map; they let the pointer through (only their buttons take taps). */
const LABEL_CLASS =
  'pointer-events-none rounded-lg bg-white/90 px-2 py-1 text-center text-sm leading-snug whitespace-nowrap tabular-nums shadow-[0_1px_4px_rgba(0,0,0,.3)] md:text-xs';

/** Above the default pin. */
const ORIGIN_OFFSET: [number, number] = [0, -44];
/** Above the path end dot. */
const CELL_OFFSET: [number, number] = [0, -10];

/** Takeoff / landing altitude, above the origin pin. */
export function OriginLabel() {
  const result = useAtomValue(resultAtom);
  const status = useAtomValue(statusAtom);
  const fmt = useUnitFormatter();
  if (!result || status.kind !== 'done' || isEmpty(result)) return null;

  return (
    <MapMarker
      lng={result.origin.lng}
      lat={result.origin.lat}
      className={LABEL_CLASS}
      anchor="bottom"
      offset={ORIGIN_OFFSET}
    >
      <b>{fmt.height(result.mode === 'fly' ? result.baseAlt : result.originElev)}</b>
    </MapMarker>
  );
}

/** Height above ground and path length at the inspected point, at the end of its flight path. */
export function CellLabel() {
  const { t } = useTranslation('glide');
  const result = useAtomValue(resultAtom);
  const status = useAtomValue(statusAtom);
  const inspection = useAtomValue(inspectionAtom);
  const compute = useSetAtom(computeConeAtom);
  const actionLabel = useOriginActionLabel();
  const fmt = useUnitFormatter();
  if (!result || !inspection || status.kind !== 'done') return null;

  const { at, cell: k } = inspection;
  const sample = sampleAt(result, at) ?? { margin: margin(result, k), path: pathLength(result, k) };

  return (
    <MapMarker lng={at.lng} lat={at.lat} className={LABEL_CLASS} anchor="bottom" offset={CELL_OFFSET}>
      <div aria-live="polite">
        <Trans
          t={t}
          i18nKey={`label.margin.${result.mode}`}
          values={{ margin: fmt.height(sample.margin) }}
          components={{ b: <b /> }}
        />
      </div>
      <div className="text-muted">{fmt.distance(sample.path, 2)}</div>
      {/* Touch screens cannot click inside the result to move the origin there, so the label offers it. */}
      {inspection.pinned && (
        <Button
          primary
          className="pointer-events-auto my-1 w-full"
          onClick={() => void compute({ lat: at.lat, lng: at.lng })}
        >
          {actionLabel}
        </Button>
      )}
    </MapMarker>
  );
}
